import React from 'react';
import { ConfirmProvider } from 'material-ui-confirm';
import { useRouter } from 'next/router';
import { ErrorProvider, InfoProvider, LoadingProvider } from '@/contexts';
import { ErrorCountsProvider } from '@/contexts/ErrorCountsContext';
import { IntegrationsProvider } from '@/contexts/IntegrationsContext';
import { OrganizationProvider } from '@/contexts/OrganizationContext';
import { UserProvider } from '@/contexts/UserContext';
import { IntercomIdentifyEffect } from '@/componentsv2/intercom/IntercomIdentifyEffect';
import { IntercomRouteEffect } from '@/componentsv2/intercom/IntercomRouteEffect';
import { OrganizationEntryGuard } from '@/session/OrganizationEntryGuard';
import { isOrgRoutePattern } from '@/session/organization';

export type OrganizationGatedPage = React.ComponentType<object> & {
  skipOrganizationGate?: boolean;
};

type OrganizationGatedProvidersProps = {
  /**
   * Set by a page via `Page.skipOrganizationGate = true`. The invitation flow
   * is reached by users who are not members of the organization yet, so gating
   * it on a membership check would block the accept-invite path outright.
   */
  page: OrganizationGatedPage;
  children: React.ReactNode;
};

/**
 * Owns the organization entry gate and the organization-scoped provider stack.
 * Lives outside `_app.tsx` so the opt-out is testable as behavior rather than
 * only as a page inventory: `_app.tsx` itself cannot be imported under jsdom.
 */
export const OrganizationGatedProviders = ({ page, children }: OrganizationGatedProvidersProps) => {
  const router = useRouter();

  if (page.skipOrganizationGate === true) return <>{children}</>;

  const isOrganizationRoute = isOrgRoutePattern(router.pathname);
  const organizationProviders = isOrganizationRoute ? (
    <OrganizationProvider>
      <IntercomIdentifyEffect />
      <IntercomRouteEffect />
      <ErrorCountsProvider>
        <IntegrationsProvider>{children}</IntegrationsProvider>
      </ErrorCountsProvider>
    </OrganizationProvider>
  ) : (
    children
  );

  return (
    <OrganizationEntryGuard>
      <ConfirmProvider>
        <ErrorProvider>
          <InfoProvider>
            <LoadingProvider>
              <UserProvider>{organizationProviders}</UserProvider>
            </LoadingProvider>
          </InfoProvider>
        </ErrorProvider>
      </ConfirmProvider>
    </OrganizationEntryGuard>
  );
};
